import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Card, Badge, Row, Col, Modal, Tabs, Tab, Spinner, ButtonGroup } from 'react-bootstrap';
import { apiClient } from '../api';
import { format } from 'date-fns';
import { Calendar, TrendingUp, AlertCircle, ChevronLeft, ChevronRight, Briefcase, Eye, ShieldAlert, MapPin } from 'lucide-react';

const UnpaidReportsView = () => {
    // Estados pestaña 1: Flujo Mensual
    const [currentDate, setCurrentDate] = useState(new Date());
    const [monthlyData, setMonthlyData] = useState(null);
    const [monthlyLoading, setMonthlyLoading] = useState(false);
    const [monthlyError, setMonthlyError] = useState(null);

    // Estados pestaña 2: Morosos Históricos
    const [historicalData, setHistoricalData] = useState([]);
    const [historicalLoading, setHistoricalLoading] = useState(false);
    const [historicalError, setHistoricalError] = useState(null);
    const [expandedClient, setExpandedClient] = useState(null);
    const [expandedTabMap, setExpandedTabMap] = useState({}); // client_id -> 'months' | 'ccs'

    // Estado Modal de Viajes Detallados (Pestaña Mensual)
    const [showTripsModal, setShowTripsModal] = useState(false);
    const [tripsModalTitle, setTripsModalTitle] = useState('');
    const [modalTrips, setModalTrips] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);

    // Cargar reporte mensual
    const loadMonthlyReport = useCallback(async (date) => {
        setMonthlyLoading(true);
        setMonthlyError(null);
        try {
            const year = date.getFullYear();
            const month = date.getMonth() + 1;
            const res = await apiClient.get(`transport/reports/unpaid/monthly`, {
                params: { year, month }
            });
            setMonthlyData(res.data);
        } catch (e) {
            setMonthlyError("Error al cargar el reporte de impagos mensual.");
        } finally {
            setMonthlyLoading(false);
        }
    }, []);

    // Cargar reporte histórico (Morosos)
    const loadHistoricalReport = useCallback(async () => {
        setHistoricalLoading(true);
        setHistoricalError(null);
        try {
            const res = await apiClient.get(`transport/reports/unpaid/historical`);
            setHistoricalData(res.data);
        } catch (e) {
            setHistoricalError("Error al cargar la morosidad histórica.");
        } finally {
            setHistoricalLoading(false);
        }
    }, []);

    // Inicializar cargas
    useEffect(() => {
        loadMonthlyReport(currentDate);
        loadHistoricalReport();
    }, [currentDate, loadMonthlyReport, loadHistoricalReport]);

    // Navegar mes
    const handlePrevMonth = () => {
        const d = new Date(currentDate);
        d.setMonth(d.getMonth() - 1);
        setCurrentDate(d);
    };

    const handleNextMonth = () => {
        const d = new Date(currentDate);
        d.setMonth(d.getMonth() + 1);
        setCurrentDate(d);
    };

    // Alternar desglose de moroso (por mes o por CC)
    const toggleExpandedTab = (clientId, tab) => {
        setExpandedTabMap(prev => ({
            ...prev,
            [clientId]: tab
        }));
    };

    // Abrir modal con los viajes específicos de un cliente en un estado mensual
    const openTripsDetailModal = async (clientId, clientName, status, statusLabel) => {
        setTripsModalTitle(`Servicios en estado ${statusLabel} - ${clientName}`);
        setModalLoading(true);
        setShowTripsModal(true);
        setModalTrips([]);
        try {
            const year = currentDate.getFullYear();
            const month = currentDate.getMonth() + 1;
            
            // Usamos un endpoint de búsqueda o bien consultamos los trips filtrados directamente del backend
            const res = await apiClient.get('transport/trips/', {
                params: {
                    client_id: clientId,
                    start_date: new Date(year, month - 1, 1).toISOString().split('T')[0],
                    end_date: new Date(year, month, 0).toISOString().split('T')[0],
                    status: 'completed,no_show,cancelled_paid',
                    limit: 2000
                }
            });
            
            // Filtrar localmente según el estado de facturación para exactitud de lógica
            const filteredTrips = res.data.filter(t => {
                // Filtrar por settled
                if (t.admin_status === 'settled') return false;
                
                // Lógica de categorización idéntica al backend
                let category = "NO_PREFACTURADO";
                if (t.client_invoice_id) {
                    const invoice = t.client_invoice;
                    if (invoice && invoice.documents) {
                        let hasInvoiceDoc = false;
                        for (const doc of invoice.documents) {
                            const docType = String(doc.type || "").toUpperCase();
                            if ((docType.includes("FACTURA") || docType.includes("INVOICE")) && doc.folio && String(doc.folio).trim()) {
                                hasInvoiceDoc = true;
                                break;
                            }
                        }
                        if (hasInvoiceDoc) category = "FACTURADO";
                        else category = "PREFACTURADO";
                    } else {
                        category = "PREFACTURADO";
                    }
                }
                
                return category === status;
            });
            
            setModalTrips(filteredTrips);
        } catch (e) {
            alert("Error al cargar el detalle de servicios.");
        } finally {
            setModalLoading(false);
        }
    };

    // Abrir modal con servicios de Particulares (todos o solo morosos)
    const openParticularesModal = async (onlyUnpaid = false) => {
        setTripsModalTitle(onlyUnpaid ? "Servicios Particulares Morosos / Pendientes de Pago" : "Servicios Particulares del Mes");
        setModalLoading(true);
        setShowTripsModal(true);
        setModalTrips([]);
        try {
            const year = currentDate.getFullYear();
            const month = currentDate.getMonth() + 1;
            
            const res = await apiClient.get('transport/trips/', {
                params: {
                    start_date: new Date(year, month - 1, 1).toISOString().split('T')[0],
                    end_date: new Date(year, month, 0).toISOString().split('T')[0],
                    status: 'completed,no_show,cancelled_paid',
                    limit: 2000
                }
            });
            
            const filteredTrips = res.data.filter(t => {
                const clientSegment = String(t.client?.segmentation || '').trim().toLowerCase();
                if (clientSegment !== 'particulares') return false;
                if (onlyUnpaid) {
                    return !t.is_paid && t.admin_status !== 'settled';
                }
                return true;
            });
            
            setModalTrips(filteredTrips);
        } catch (e) {
            alert("Error al cargar los servicios particulares.");
        } finally {
            setModalLoading(false);
        }
    };

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(val);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'NO_PREFACTURADO':
                return <Badge bg="danger" className="text-white">NO PREFACTURADO</Badge>;
            case 'PREFACTURADO':
                return <Badge bg="warning" className="text-dark">PREFACTURADO</Badge>;
            case 'FACTURADO':
                return <Badge bg="success" className="text-white">FACTURADO</Badge>;
            default:
                return <Badge bg="secondary">PENDIENTE</Badge>;
        }
    };

    // Nombre del mes formateado
    const monthsNames = [
        "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
        "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
    ];
    const monthLabel = `${monthsNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;

    return (
        <div className="container-fluid py-4" style={{ backgroundColor: '#0f172a', minHeight: '100vh', color: '#f8fafc' }}>
            {/* Header Módulo */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="fw-bold mb-1 text-white"><ShieldAlert size={26} className="me-2 text-danger align-bottom"/> Gestión de Impagos y Morosidad</h3>
                    <p className="text-muted small mb-0">Control total del flujo de cobranza, deudas históricas por cliente y desgloses contables.</p>
                </div>
            </div>

            <Tabs defaultActiveKey="flujo_mensual" id="unpaid_reports_tabs" className="mb-4 custom-tabs-reports">
                {/* PESTAÑA 1: FLUJO MENSUAL */}
                <Tab eventKey="flujo_mensual" title={<span><TrendingUp size={16} className="me-2"/> Flujo Mensual</span>}>
                    
                    {/* Control de Navegación de Mes y Venta Total */}
                    <Card className="border-0 shadow-sm mb-4" style={{ backgroundColor: '#1e293b' }}>
                        <Card.Body className="d-flex flex-wrap justify-content-between align-items-center py-3 gap-3">
                            <Button variant="outline-light" size="sm" onClick={handlePrevMonth} className="border-0 hover-bg-slate">
                                <ChevronLeft size={20} />
                            </Button>
                            <div className="d-flex flex-wrap align-items-center justify-content-center gap-3">
                                <h4 className="fw-bold mb-0 text-uppercase tracking-wider text-white d-flex align-items-center">
                                    <Calendar size={20} className="me-2 text-info"/>
                                    {monthLabel}
                                </h4>
                                {monthlyData?.summary?.total_sales !== undefined && (
                                    <div className="d-flex align-items-center gap-2 px-3 py-1 rounded-pill border border-secondary border-opacity-40" style={{ backgroundColor: '#0f172a' }}>
                                        <TrendingUp size={15} className="text-success" />
                                        <span className="small text-muted text-uppercase fw-semibold" style={{ fontSize: '11px' }}>Venta Total:</span>
                                        <span className="fw-bold text-white" style={{ fontSize: '15px' }}>{formatCurrency(monthlyData.summary.total_sales)}</span>
                                        <Badge bg="secondary" className="text-light ms-1" style={{ fontSize: '10px' }}>{monthlyData.summary.total_sales_trips} viajes</Badge>
                                    </div>
                                )}
                            </div>
                            <Button variant="outline-light" size="sm" onClick={handleNextMonth} className="border-0 hover-bg-slate">
                                <ChevronRight size={20} />
                            </Button>
                        </Card.Body>
                    </Card>

                    {monthlyLoading ? (
                        <div className="text-center py-5"><Spinner animation="border" variant="info"/></div>
                    ) : monthlyError ? (
                        <div className="alert alert-danger">{monthlyError}</div>
                    ) : monthlyData ? (
                        <>
                            <Row className="g-3 mb-4">
                                <Col md={4}>
                                    <Card className="border-0 shadow-sm text-center" style={{ backgroundColor: '#1e293b', borderLeft: '4px solid #38bdf8' }}>
                                        <Card.Body className="py-3">
                                            <div className="small fw-bold text-uppercase mb-1" style={{ fontSize: '10px', color: '#cbd5e1' }}>Cobros Particulares</div>
                                            <h3 className="fw-bold text-info mb-1">{formatCurrency(monthlyData.summary.particulares.amount)}</h3>
                                            <div className="d-flex justify-content-center align-items-center gap-2 mb-2">
                                                <Badge bg="info" className="text-dark">{monthlyData.summary.particulares.trips_count} Servicios</Badge>
                                            </div>
                                            {/* Desglose Pagados vs Morosos */}
                                            {monthlyData.summary.particulares.paid_amount !== undefined && (
                                                <div className="d-flex justify-content-center gap-2 pt-2 border-top border-secondary border-opacity-25" style={{ fontSize: '11px' }}>
                                                    <span className="badge bg-success bg-opacity-25 text-success border border-success border-opacity-50" title="Particulares Pagados">
                                                        Pagado: {formatCurrency(monthlyData.summary.particulares.paid_amount)} ({monthlyData.summary.particulares.paid_trips})
                                                    </span>
                                                    <button 
                                                        type="button"
                                                        onClick={() => openParticularesModal(true)}
                                                        className="btn btn-link p-0 text-decoration-none"
                                                        title="Ver particulares impagos/morosos"
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <span className="badge bg-danger text-white border border-danger shadow-sm">
                                                            Moroso: {formatCurrency(monthlyData.summary.particulares.unpaid_amount)} ({monthlyData.summary.particulares.unpaid_trips}) 🔍
                                                        </span>
                                                    </button>
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={4}>
                                    <Card className="border-0 shadow-sm text-center" style={{ backgroundColor: '#1e293b', borderLeft: '4px solid #818cf8' }}>
                                        <Card.Body className="py-3">
                                            <div className="small fw-bold text-uppercase mb-1" style={{ fontSize: '10px', color: '#cbd5e1' }}>Cobros Convenios</div>
                                            <h3 className="fw-bold text-indigo mb-1" style={{ color: '#818cf8' }}>{formatCurrency(monthlyData.summary.convenios.amount)}</h3>
                                            <div className="d-flex justify-content-center align-items-center gap-2 mb-2">
                                                <Badge bg="indigo" className="text-white" style={{ backgroundColor: '#6366f1' }}>{monthlyData.summary.convenios.trips_count} Servicios</Badge>
                                            </div>
                                            <div className="text-muted pt-2 border-top border-secondary border-opacity-25 small" style={{ fontSize: '11px' }}>
                                                Cartera B2B en cobranza abierta
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={4}>
                                    <Card className="border-0 shadow-sm text-center" style={{ backgroundColor: '#1e293b', borderLeft: '4px solid #f43f5e' }}>
                                        <Card.Body className="py-3">
                                            <div className="small fw-bold text-uppercase mb-1" style={{ fontSize: '10px', color: '#cbd5e1' }}>Total Deuda del Mes</div>
                                            <h3 className="fw-bold text-danger mb-1">{formatCurrency(monthlyData.summary.total_unpaid)}</h3>
                                            <div className="d-flex justify-content-center align-items-center gap-2 mb-2">
                                                <Badge bg="danger" className="text-white">{monthlyData.summary.total_trips} Servicios Abiertos</Badge>
                                            </div>
                                            {/* % sobre la Venta Total */}
                                            {monthlyData.summary.unpaid_percentage !== undefined && (
                                                <div className="d-flex justify-content-center align-items-center gap-2 pt-2 border-top border-secondary border-opacity-25" style={{ fontSize: '11px' }}>
                                                    <span className="badge bg-danger bg-opacity-25 text-danger border border-danger border-opacity-50">
                                                        {monthlyData.summary.unpaid_percentage}% por cobrar
                                                    </span>
                                                    <span className="text-muted">|</span>
                                                    <span className="badge bg-success bg-opacity-25 text-success border border-success border-opacity-50">
                                                        {monthlyData.summary.collected_percentage}% recaudado
                                                    </span>
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Embudo de 3 Columnas Contables */}
                            <Row className="g-4">
                                {[
                                    { key: "NO_PREFACTURADO", title: "No Prefacturado", border: "danger", bg: "danger", textClass: "text-danger" },
                                    { key: "PREFACTURADO", title: "Prefacturado", border: "warning", bg: "warning", textClass: "text-warning" },
                                    { key: "FACTURADO", title: "Facturado", border: "success", bg: "success", textClass: "text-success" }
                                ].map((col) => {
                                    const flowCol = monthlyData.flow[col.key] || { amount: 0, trips_count: 0, percentage_of_month: 0, clients: [] };
                                    return (
                                        <Col key={col.key} lg={4}>
                                            <Card className="border-0 shadow h-100" style={{ backgroundColor: '#1e293b' }}>
                                                {/* Header Estado */}
                                                <Card.Header className={`bg-transparent border-bottom border-secondary d-flex justify-content-between align-items-center py-3`}>
                                                    <span className={`fw-bold text-uppercase ${col.textClass}`} style={{ fontSize: '14px', letterSpacing: '0.5px' }}>
                                                        ● {col.title}
                                                    </span>
                                                    <Badge bg={col.bg} className={col.key === 'PREFACTURADO' ? 'text-dark' : 'text-white'}>
                                                        {flowCol.percentage_of_month}% del mes
                                                    </Badge>
                                                </Card.Header>
                                                
                                                {/* KPI Estado */}
                                                <Card.Body className="bg-slate-900 border-bottom border-secondary py-3 text-center" style={{ backgroundColor: '#0f172a' }}>
                                                    <h3 className={`fw-bold mb-0 ${col.textClass}`}>{formatCurrency(flowCol.amount)}</h3>
                                                    <span className="small text-muted">{flowCol.trips_count} Servicios</span>
                                                </Card.Body>

                                                {/* Lista Clientes */}
                                                <Card.Body className="p-0 overflow-auto" style={{ maxHeight: '400px' }}>
                                                    {flowCol.clients.length === 0 ? (
                                                        <div className="text-center text-muted py-5 small">Sin movimientos en este estado.</div>
                                                    ) : (
                                                        flowCol.clients.map((c) => (
                                                            <div key={c.client_id} className="d-flex justify-content-between align-items-center px-3 py-3 border-bottom border-secondary border-opacity-20 hover-bg-slate-800 transition-all">
                                                                <div className="pe-2">
                                                                    <div className="fw-bold small text-white">{c.client_name}</div>
                                                                    <div className="text-muted" style={{ fontSize: '11px' }}>{c.trips_count} viajes ({c.percentage_of_category}%)</div>
                                                                </div>
                                                                <div className="text-end d-flex align-items-center gap-2">
                                                                    <span className="fw-bold small text-white">{formatCurrency(c.amount)}</span>
                                                                    <Button 
                                                                        variant="outline-secondary" 
                                                                        size="sm" 
                                                                        className="p-1 border-0 text-muted hover-text-white"
                                                                        onClick={() => openTripsDetailModal(c.client_id, c.client_name, col.key, col.title)}
                                                                    >
                                                                        <Eye size={14} />
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        ))
                                                    )}
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </>
                    ) : null}
                </Tab>

                {/* PESTAÑA 2: DEUDA HISTÓRICA / MOROSOS */}
                <Tab eventKey="deuda_historica" title={<span><AlertCircle size={16} className="me-2"/> Morosos Históricos</span>}>
                    
                    {historicalLoading ? (
                        <div className="text-center py-5"><Spinner animation="border" variant="info"/></div>
                    ) : historicalError ? (
                        <div className="alert alert-danger">{historicalError}</div>
                    ) : (
                        <Row className="g-4">
                            <Col md={12}>
                                <Card className="border-0 shadow-sm" style={{ backgroundColor: '#1e293b' }}>
                                    <Card.Header className="bg-dark text-white py-3 fw-bold">
                                        <Briefcase size={16} className="me-2 text-info align-text-bottom"/> Deuda Abierta Consolidada por Cliente
                                    </Card.Header>
                                    <Card.Body className="p-0">
                                        <Table hover responsive size="sm" variant="dark" className="mb-0 align-middle" style={{ backgroundColor: '#1e293b', borderCollapse: 'collapse' }}>
                                            <thead className="bg-slate-900 text-muted" style={{ backgroundColor: '#0f172a', borderBottom: '2px solid #334155' }}>
                                                <tr>
                                                    <th className="px-4 py-3 text-muted fw-semibold">Cliente</th>
                                                    <th className="py-3 text-muted fw-semibold">Segmento</th>
                                                    <th className="py-3 text-muted fw-semibold">Estado Principal</th>
                                                    <th className="py-3 text-muted fw-semibold text-center">Viajes Abiertos</th>
                                                    <th className="py-3 text-muted fw-semibold text-end">Deuda Total</th>
                                                    <th className="py-3 px-4 text-muted fw-semibold text-end">Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {historicalData.length === 0 ? (
                                                    <tr><td colSpan="6" className="text-center py-5 text-muted">No se registran clientes con deudas abiertas.</td></tr>
                                                ) : (
                                                    historicalData.map((client) => {
                                                        const isExpanded = expandedClient === client.client_id;
                                                        const subTab = expandedTabMap[client.client_id] || 'months';

                                                        return (
                                                            <React.Fragment key={client.client_id}>
                                                                {/* Fila Principal */}
                                                                <tr className="border-bottom border-slate-700 hover-bg-slate-800 transition-all" style={{ borderBottom: '1px solid #334155' }}>
                                                                    <td className="px-4 py-3 fw-bold">{client.client_name}</td>
                                                                    <td><Badge bg="secondary" style={{ backgroundColor: '#475569' }}>{client.segmentation}</Badge></td>
                                                                    <td>{getStatusBadge(client.primary_status)}</td>
                                                                    <td className="text-center fw-bold">{client.trips_count}</td>
                                                                    <td className="text-end fw-bold text-danger">{formatCurrency(client.total_debt)}</td>
                                                                    <td className="text-end px-4">
                                                                        <Button 
                                                                            variant="outline-info" 
                                                                            size="sm" 
                                                                            onClick={() => setExpandedClient(isExpanded ? null : client.client_id)}
                                                                            className="fw-bold"
                                                                            style={{ fontSize: '11px', padding: '4px 10px' }}
                                                                        >
                                                                            {isExpanded ? "Ocultar Detalle" : "Desglosar Deuda"}
                                                                        </Button>
                                                                    </td>
                                                                </tr>

                                                                {/* Panel de Desglose Expandido */}
                                                                {isExpanded && (
                                                                    <tr style={{ backgroundColor: '#0f172a' }}>
                                                                        <td colSpan="6" className="p-4">
                                                                            <div className="p-3 rounded border border-secondary border-opacity-30" style={{ backgroundColor: '#1e293b' }}>
                                                                                
                                                                                {/* Alternador de desglose */}
                                                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                                                    <div className="small fw-bold text-uppercase" style={{ color: '#cbd5e1' }}>Desglose de Deuda: {client.client_name}</div>
                                                                                    <ButtonGroup size="sm">
                                                                                        <Button 
                                                                                            variant={subTab === 'months' ? 'info' : 'outline-light'} 
                                                                                            onClick={() => toggleExpandedTab(client.client_id, 'months')}
                                                                                            className="fw-bold"
                                                                                        >
                                                                                            📅 Por Meses
                                                                                        </Button>
                                                                                        <Button 
                                                                                            variant={subTab === 'ccs' ? 'info' : 'outline-light'} 
                                                                                            onClick={() => toggleExpandedTab(client.client_id, 'ccs')}
                                                                                            className="fw-bold"
                                                                                        >
                                                                                            🏢 Por Centro de Costo
                                                                                        </Button>
                                                                                    </ButtonGroup>
                                                                                </div>

                                                                                {/* Contenido Desglose por Meses */}
                                                                                {subTab === 'months' && (
                                                                                    <div>
                                                                                        {client.debt_by_month.length === 0 ? (
                                                                                            <div className="text-center py-3" style={{ color: '#94a3b8' }}>No hay desglose por meses disponible.</div>
                                                                                        ) : (
                                                                                            <Row className="g-2">
                                                                                                {client.debt_by_month.map((m, idx) => (
                                                                                                    <Col key={idx} md={4}>
                                                                                                        <div className="d-flex justify-content-between align-items-center px-3 py-2 bg-dark rounded border border-secondary border-opacity-20" style={{ backgroundColor: '#0f172a' }}>
                                                                                                            <span className="small text-light">{m.month_label}</span>
                                                                                                            <span className="fw-bold small text-warning">{formatCurrency(m.amount)}</span>
                                                                                                        </div>
                                                                                                    </Col>
                                                                                                ))}
                                                                                            </Row>
                                                                                        )}
                                                                                    </div>
                                                                                )}

                                                                                {/* Contenido Desglose por Centro de Costo */}
                                                                                {subTab === 'ccs' && (
                                                                                    <div>
                                                                                        {client.debt_by_cost_center.length === 0 ? (
                                                                                            <div className="text-center py-3" style={{ color: '#94a3b8' }}>No hay centros de costo con deudas abiertas.</div>
                                                                                        ) : (
                                                                                            <Row className="g-2">
                                                                                                {client.debt_by_cost_center.map((cc, idx) => (
                                                                                                    <Col key={idx} md={4}>
                                                                                                        <div className="d-flex justify-content-between align-items-center px-3 py-2 bg-dark rounded border border-secondary border-opacity-20" style={{ backgroundColor: '#0f172a' }}>
                                                                                                            <div className="pe-1 text-truncate">
                                                                                                                <span className="small fw-bold d-block text-truncate text-light" title={cc.cost_center_name}>{cc.cost_center_name}</span>
                                                                                                                <span style={{ fontSize: '10px', color: '#94a3b8' }}>Código: {cc.cost_center_code}</span>
                                                                                                            </div>
                                                                                                            <span className="fw-bold small text-warning">{formatCurrency(cc.amount)}</span>
                                                                                                        </div>
                                                                                                    </Col>
                                                                                                ))}
                                                                                            </Row>
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </React.Fragment>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </Table>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}
                </Tab>
            </Tabs>

            {/* MODAL DETALLE DE VIAJES DE UN CLIENTE */}
            <Modal show={showTripsModal} onHide={() => setShowTripsModal(false)} size="xl" centered>
                <Modal.Header closeButton className="bg-dark text-white border-secondary">
                    <Modal.Title className="small fw-bold text-white">{tripsModalTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-slate-900 text-white p-0" style={{ backgroundColor: '#0f172a', maxHeight: '70vh', overflowY: 'auto' }}>
                    {modalLoading ? (
                        <div className="text-center py-5"><Spinner animation="border" variant="info" /></div>
                    ) : modalTrips.length === 0 ? (
                        <div className="text-center py-5 text-muted">No se encontraron servicios en este estado contable para el mes consultado.</div>
                    ) : (
                        <Table hover responsive size="sm" variant="dark" className="mb-0 align-middle" style={{ fontSize: '13px', backgroundColor: '#0f172a' }}>
                            <thead className="bg-dark text-muted" style={{ backgroundColor: '#1e293b', borderBottom: '2px solid #334155' }}>
                                <tr>
                                    <th className="px-3 py-2">ID</th>
                                    <th>Fecha</th>
                                    <th>Pasajero</th>
                                    <th>Origen / Destino</th>
                                    <th>Centro Costo</th>
                                    <th className="text-end">Tarifa</th>
                                    <th className="text-center">Estado Flujo</th>
                                    <th>Ref. Booking</th>
                                </tr>
                            </thead>
                            <tbody>
                                {modalTrips.map((t) => (
                                    <tr key={t.id} className="border-bottom border-slate-800" style={{ borderBottom: '1px solid #1e293b' }}>
                                        <td className="px-3 py-2 fw-bold text-info">#{t.id}</td>
                                        <td>{t.scheduled_time ? format(new Date(t.scheduled_time), 'dd/MM/yyyy HH:mm') : 'N/A'}</td>
                                        <td>
                                            <div className="fw-bold">{t.passenger_name}</div>
                                            <div className="text-muted" style={{ fontSize: '11px' }}>{t.passenger_phone || 'Sin Teléfono'}</div>
                                        </td>
                                        <td>
                                            <div className="text-truncate" style={{ maxWidth: '200px' }} title={t.pickup_address}>
                                                <MapPin size={12} className="text-danger me-1"/>
                                                {t.pickup_address}
                                            </div>
                                            <div className="text-truncate text-muted" style={{ maxWidth: '200px', fontSize: '11px' }} title={t.dropoff_address}>
                                                <MapPin size={12} className="text-success me-1"/>
                                                {t.dropoff_address}
                                            </div>
                                        </td>
                                        <td>
                                            {t.cost_center ? (
                                                <div>
                                                    <span className="fw-bold d-block">{t.cost_center.name}</span>
                                                    <span className="text-muted" style={{ fontSize: '11px' }}>Cód: {t.cost_center.code}</span>
                                                </div>
                                            ) : (
                                                <span className="text-muted">{t.client?.name || 'Particular'}</span>
                                            )}
                                        </td>
                                        <td className="text-end fw-bold text-white">{formatCurrency(t.final_price ?? t.estimated_price)}</td>
                                        <td className="text-center">
                                            {String(t.client?.segmentation || '').trim().toLowerCase() === 'particulares' ? (
                                                t.is_paid || t.admin_status === 'settled' ? (
                                                    <Badge bg="success" className="text-white">PAGADO {t.payment_method ? `(${t.payment_method})` : ''}</Badge>
                                                ) : (
                                                    <Badge bg="danger" className="text-white">IMPAGO / MOROSO</Badge>
                                                )
                                            ) : (
                                                getStatusBadge(t.client_invoice_id ? "PREFACTURADO" : "NO_PREFACTURADO")
                                            )}
                                        </td>
                                        <td><Badge bg="dark" className="text-muted border border-secondary">{t.external_reference || 'N/A'}</Badge></td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Modal.Body>
                <Modal.Footer className="bg-dark border-secondary">
                    <Button variant="secondary" onClick={() => setShowTripsModal(false)}>Cerrar</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default UnpaidReportsView;
